import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface EmailVerificationDialogProps {
  open: boolean;
  email: string;
  onClose: () => void;
}

export const EmailVerificationDialog = ({ open, email, onClose }: EmailVerificationDialogProps) => {
  const { t } = useTranslation('auth');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.light',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <EmailIcon sx={{ fontSize: 48 }} />
          </Box>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {t('emailVerification.title', 'Check Your Email')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" paragraph>
            {t('emailVerification.message', {
              defaultValue: 'We\'ve sent a verification email to:',
            })}
          </Typography>
          
          <Typography 
            variant="h6" 
            color="primary" 
            sx={{ 
              bgcolor: 'primary.light', 
              p: 1.5, 
              borderRadius: 1, 
              mb: 3,
              wordBreak: 'break-word'
            }}
          >
            {email}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, textAlign: 'left' }}>
            <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t('emailVerification.step1', 'Click the verification link in the email to activate your account')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, textAlign: 'left' }}>
            <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t('emailVerification.step2', 'Once verified, you can sign in to your account')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3, textAlign: 'left' }}>
            <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t('emailVerification.step3', 'The verification link will expire in 24 hours')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              bgcolor: 'info.light', 
              p: 2, 
              borderRadius: 1,
              textAlign: 'left'
            }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {t('emailVerification.note', 'Didn\'t receive the email?')}
            </Typography>
            <Typography variant="body2">
              {t('emailVerification.checkSpam', 'Check your spam folder or try registering again.')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{ minWidth: 200 }}
        >
          {t('emailVerification.gotIt', 'Got It')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
